<template>
    <li class="commentsWithReplies">
        <TransitionEffect>
            <Dialog
                v-if="deleteCommentDialog"
                appearance="red"
                primaryIcon="delete"
                @primary="deleteComment"
                @secondary="deleteCommentDialog=false"
                @close="deleteCommentDialog=false">
                <template slot="header">{{ siteContent.deleteCommentDialog }}</template>
                <template slot="body">{{ siteContent.deleteWithAllReplies }} {{ siteContent.thinkTwice }}</template>
                <template slot="secondary">{{ siteContent.cancel }}</template>
                <template slot="primary">{{ siteContent.delete }}</template>
            </Dialog>
        </TransitionEffect>
        <div class="comment">
            <div class="head">
                <Contributor
                    v-if="!loading"
                    :slug="user.slug"
                    :avatar="user.avatar"
                    :name="user.name">
                </Contributor>

                <time class="timeStamp">
                    {{ timestamp.toDate() | moment("from", "now") }}
                </time>
            </div>
            
            <p class="content">{{ content }}</p>
            <div class="actions">
                <a class="replyButton"
                    v-if="auth.id"
                    tabindex="0"
                    @click="handleReply">
                    {{ siteContent.reply }}
                </a>
                <a class="replyButton"
                    v-if="auth.id === writtenBy || permissions.isAdmin"
                    tabindex="0"
                    @click="deleteCommentDialog = true">
                    {{ siteContent.delete }}
                </a>
            </div>
        </div>
        <ol class="replySection">
            <Reply v-for="reply in replies"
                :key="reply.id"
                :collection="collection"
                :reply="reply"
                :parentId="parentId"
                @replyTo="setAddressee">
            </Reply>
        </ol>
        <div class="replyInputWrapper"
            ref="replyInput">
            <PostReply v-if="postReplyVisible"
                :collection="collection"
                :parentId="parentId"
                :user="user"
                :notificationDetails="notificationDetails"
                :documentId="documentId"
                @replyPosted="handleReplyPost"/>
        </div>
    </li>
</template>

<script>
    import { db } from '@/firebase'

    import Reply       from '@/components/comments/Reply'
    import PostReply   from '@/components/comments/PostReply'
    import Contributor from '@/components/entry/Contributor'

    export default {
        name: 'Comment',
        components: {
            Reply,
            PostReply,
            Contributor
        },
        props: {
            collection: {
                type: String,
                required: true
            },
            content: {
                type: String,
                required: true
            },
            writtenBy: {
                type: String,
                required: true
            },
            timestamp: {
                type: Object,
                required: true
            },
            parentId: {
                type: String,
                required: true
            },
            documentId: {
                type: String,
                required: true
            },
            notificationDetails: {
                type: Object,
                required: true
            }
        },
        data() {
            return {
                user: { },
                replies: [ ],
                postReplyVisible: false,
                deleteCommentDialog: false,
                loading: true
            }
        },
        computed: {
            avatar() {
                if(this.user.avatar) {
                    return this.user.avatar
                } else {
                    return this.constants.defaults.avatar
                }
            }
        },
        async mounted() {
            const userRef = await db
                .collection('users')
                .doc(this.writtenBy)
                .get()

            this.user = {
                ...userRef.data(),
                id: this.writtenBy
            }
            this.loading = false

            this.fetchReplies()
        },
        methods: {
            async fetchReplies() {
                try {
                    await db
                    .collection(this.collection)
                    .doc(this.parentId)
                    .collection('replies')
                    .orderBy('timestamp', 'asc')
                    .onSnapshot(snapshot => {
                        const replies = snapshot.docs
                        .map(doc => {
                            return { 
                                id: doc.id,
                                ...doc.data()
                            }
                        })

                        this.replies = replies

                        return () => cleanUp()
                    })
                } catch(error) {
                    console.log(error)
                }
            },
            async deleteComment() {
                try {
                    if(this.replies.length !== 0) {
                        for(let i = 0; i < this.replies.length; i++) {
                            await db
                                .collection(this.collection)
                                .doc(this.parentId)
                                .collection('replies')
                                .doc(this.replies[i].id)
                                .delete()

                            console.log('Reply deleted.')
                        }
                    }

                    await db
                        .collection(this.collection)
                        .doc(this.parentId)
                        .delete()

                        console.log('Comment deleted.')

                    this.$toasted.show(this.siteContent.commentSuccessfullyDeleted)
                } catch(error) {
                    console.log(error)
                }
            },
            handleReplyPost() {
                this.postReplyVisible = false
            },
            handleReply() {
                this.postReplyVisible = !this.postReplyVisible

                if(this.postReplyVisible) {
                    this.$nextTick(() => this.$refs.replyInput.scrollIntoView({ behavior: 'smooth', block: 'center' }))
                }
            },
            setAddressee(user) {
                this.user = user
                this.postReplyVisible = !this.postReplyVisible

                if(this.postReplyVisible) {
                    this.$nextTick(() => this.$refs.replyInput.scrollIntoView({ behavior: 'smooth', block: 'center' }))
                }
            }
        }
    }
</script>

<style scoped lang="scss">
    @import '../../scss/variables';

    .commentsWithReplies {
        margin-bottom: 2rem;
        list-style: none;
    }

    .comment {
        margin-bottom: 2rem;
    }

    .head {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .timeStamp {
        font-size: 1rem;
        color: var(--color-grey-500);
    }

    .replyButton {
        color: var(--color-grey-500);
        margin-left: 1rem;
        font-size: 1rem;
        cursor: pointer;
        padding: 0.25rem 0.5rem;
        border-radius: 0.5rem;
    }

    .content {
        margin: 0.5rem 0 0 3rem;
        text-align: left;
    }

    .actions {
        display: flex;
        margin-left: 1.5rem;
    }

    .replySection {
        padding: 0;
        margin: 0;
    }
</style>